/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { GetCustomerInput, createObservableDataAction, getCustomerActionDataAction } from '@msdyn365-commerce/action-internal';
import { IAction, IActionContext, versionGte } from '@msdyn365-commerce/core-internal';
import { Customer, ProductSearchCriteria, ProductSearchResult, QueryResultSettings, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getProductImageUrls } from '../../utils/product-list-utils';
import { ProductListInput } from '../hydrator-action-inputs';
import {
    getInventorySorting,
    getInventoryInStockSortableRefinerValue,
    ProductListInventoryFilteringOptions
} from '../utils/inventory-util';

export const getProductsByPromotionList = async (input: ProductListInput, context: IActionContext): Promise<ProductSearchResult[]> => {
    const rsVersion =
        !process.env.MSDyn365Commerce_RSVERSION || process.env.MSDyn365Commerce_RSVERSION === '--'
            ? '0.0'
            : process.env.MSDyn365Commerce_RSVERSION;
    const APIEnabedVersion = versionGte(rsVersion, '9.48');
    if (!APIEnabedVersion) {
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- Need for GetProductPromotions API.,
    const productsDataActions = require('@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g');

    // If the Discount Offer Id is not verified.
    if (!input.listMetadata.discountIds || (input.listMetadata.discountIds && input.listMetadata.discountIds.length === 0)) {
        context.telemetry.debug('Discount Offer Id should be verified');
        throw new Error('Discount Offer Id should be verified');
    }

    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: input.listMetadata.pageSize || 10
        }
    };

    const customer: Customer = await getCustomerActionDataAction(new GetCustomerInput(context.requestContext.apiSettings, ''), context);
    const customerAccountNumber = customer?.AccountNumber || context.requestContext.user.customerAccountNumber;

    const productSearchCriteria: ProductSearchCriteria = {
        Ids: input.listMetadata.productIds,
        // @ts-ignore
        CustomerAccountNumber: customerAccountNumber,
        RecommendationListId: input.listMetadata.recommendationListId,
        Context: {
            ChannelId: +context.requestContext.apiSettings.channelId,
            CatalogId: +input.catalogId
        },
        SkipVariantExpansion: true,
        IncludeAttributes: true
    };

    const channelInventoryConfiguration = {};
    if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS) {
        const inventoryInStockRefinerValue = getInventoryInStockSortableRefinerValue(channelInventoryConfiguration);
        if (inventoryInStockRefinerValue) {
            productSearchCriteria.Refinement = [inventoryInStockRefinerValue];
        }
    }

    /**
     * Sort by inventory availability:
     * For other type expect editorial: it's supported since 9.37.
     * For editorial list type: it's supported since 9.44 since it depends on fix from retail server which is fixed on 9.44. But logic here is the same as other types.
     */
    if (context.requestContext.app.config?.productListInventoryDisplay === ProductListInventoryFilteringOptions.SortOOS) {
        const sorting = getInventorySorting(channelInventoryConfiguration);
        if (sorting) {
            queryResultSettings.Sorting = sorting;
        }
    }

    return (
        productsDataActions
            .getProductPromotionsAsync(
                {
                    callerContext: context,
                    queryResultSettings: queryResultSettings
                },
                input.listMetadata.discountIds,
                {
                    HeaderContext: {
                        ChannelId: context.requestContext.apiSettings.channelId,
                        CustomerAccountNumber: customerAccountNumber
                    },
                    LineContexts: [
                        {
                            CatalogId: input.catalogId
                        }
                    ]
                }
            )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ProductPromotion is introduced in 9.48 in retail proxy.
            .then((productPromotions: any[]) => {
                if (productPromotions.length > 0) {
                    const productIds: number[] = productPromotions.map(p => p.ProductId);
                    productSearchCriteria.Ids = productIds;
                    return productsDataActions.searchByCriteriaAsync(
                        {
                            callerContext: context,
                            queryResultSettings
                        },
                        productSearchCriteria
                    );
                } else {
                    throw new Error(`No product retrieved for configured discounts: ${input.listMetadata.discountIds}`);
                }
            })
            .then((productSearchResult: ProductSearchResult[]) => {
                context.telemetry.debug('Products returned by getProductsByPromotionList action', productSearchResult);
                return getProductImageUrls(productSearchResult, context.requestContext.apiSettings);
            })
            .catch(error => {
                context.telemetry.error(`Error running getProductsByPromotionList action: ${error}`);
                throw new Error(error);
            })
    );
};

export const getProductsByPromotionListAction = createObservableDataAction({
    action: <IAction<ProductSearchResult[]>>getProductsByPromotionList,
    id: '@msdyn365-commerce/products-by-promotion'
});
